import ReactGA from 'react-ga4';
import posthog from 'posthog-js';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-0QDR9V316T';
export const CONSENT_STORAGE_KEY = 'perrinos_consent';
let isInitialized = false;
let consentGranted = false;

export const isConsentGranted = () => consentGranted;

export const updateConsent = (granted) => {
  consentGranted = Boolean(granted);

  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'analytics_storage': consentGranted ? 'granted' : 'denied',
      'ad_storage': consentGranted ? 'granted' : 'denied'
    });
  }

  if (typeof window !== 'undefined' && window.posthog) {
    if (consentGranted) {
      window.posthog.opt_in_capturing();
    } else {
      window.posthog.opt_out_capturing();
    }
  }
};

export const initAnalytics = () => {
  if (isInitialized) return;
  if (typeof window === 'undefined') return;

  try {
    if (window.gtag) {
      window.gtag('consent', 'default', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied'
      });
    }

    ReactGA.initialize(GA_MEASUREMENT_ID);
    posthog.init(process.env.REACT_APP_POSTHOG_KEY || 'phc_dummy_key_esn_phalanx', {
      api_host: 'https://eu.i.posthog.com',
      autocapture: false,
      capture_pageview: false,
      persistence: 'localStorage',
      cookieless_mode: 'on_reject'
    });

    isInitialized = true;
  } catch (err) {
    console.warn('Analytics initialization failed:', err);
  }
};

export const trackPageView = (path) => {
  if (!consentGranted) return;
  if (!isInitialized) initAnalytics();
  try {
    ReactGA.send({ hitType: 'pageview', page: path });
    posthog.capture('$pageview', { '$current_url': window.location.origin + path });
  } catch (e) {
    console.warn('trackPageView failed:', e);
  }
};

export const trackEvent = (eventName, properties = {}) => {
  if (!consentGranted) return;
  if (!isInitialized) initAnalytics();
  try {
    ReactGA.event({
      category: properties.category || 'Engagement',
      action: eventName,
      label: properties.label
    });
    posthog.capture(eventName, { ...properties, '$timestamp': new Date().toISOString() });
  } catch (e) {
    console.warn('trackEvent failed:', e);
  }
};

export const identifyUser = (user) => {
  if (!isInitialized) initAnalytics();
  try {
    if (user && user.id) {
      posthog.identify(user.id, {
        email: user.email,
        role: user.role,
        name: user.name,
        full_name: user.full_name
      });
    } else {
      posthog.reset();
    }
  } catch (e) {
    console.warn('identifyUser failed:', e);
  }
};

export const checkFeatureFlag = (flagKey, defaultValue = false) => {
  if (!consentGranted) return defaultValue;
  if (!isInitialized) initAnalytics();
  try {
    return posthog.isFeatureEnabled(flagKey) || defaultValue;
  } catch (e) {
    console.warn('checkFeatureFlag failed:', e);
    return defaultValue;
  }
};

// Semantic wrappers over trackEvent. Call sites across the app reference
// these by name (SignaturePanel, AiMissionCreate, RoiCalculatorSection,
// ProposalPublic, Register) — keep them as thin delegations so there's one
// place (trackEvent) that owns the actual ReactGA/PostHog calls.
const analytics = {
  initAnalytics,
  trackPageView,
  trackEvent,
  identifyUser,
  checkFeatureFlag,
  updateConsent,
  isConsentGranted,
  track: trackEvent,
  signatureRegenerated: (props) => trackEvent('signature_regenerated', props),
  briefGenerated: (props) => trackEvent('brief_generated', props),
  proposalViewed: (props) => trackEvent('proposal_viewed', props),
  roiSliderMoved: (props) => trackEvent('roi_slider_moved', props),
  roiCtaClicked: (props) => trackEvent('roi_cta_clicked', props),
  signupCompleted: (props) => trackEvent('signup_completed', props)
};

export default analytics;
export { analytics };
